import { focusOutline } from 'shared/assets/styles';
import { typographyToCss } from 'shared/lib/mui';
import styled, { css, keyframes } from 'styled-components';

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;

  overflow: hidden;

  & button:focus {
    ${focusOutline};
  }
`;

export const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;

  display: flex;
  flex-direction: column;

  overflow-y: auto;
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1)};

  padding: ${({ theme }) => `${theme.spacing(3)}`};
`;

export const TitleColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

export const Title = styled.h2`
  ${({ theme }) => typographyToCss(theme.typography.subtitle2)};
  margin: 0;
  overflow-wrap: anywhere;
`;

// the actions popup opens off this box, so it must not scroll away with the title
export const HeaderMenuAnchor = styled.div`
  position: relative;
  flex-shrink: 0;
`;

// The field grid: column 1 is the icon+label, column 2 the control. Rows are
// `display: contents` so every label lines up on one column across the popup.
export const Body = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(2)};
  row-gap: ${({ theme }) => theme.spacing(2)};

  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
`;

export const Section = styled.div`
  display: contents;
`;

// `display: contents` drops this box, so the Label and the value become Body's own grid
// items — per-row alignment has to be set on those children, never on this element.
export const Item = styled.div<{
  $autoWidthField?: boolean;
  $alignTop?: boolean;
}>`
  display: contents;

  ${({ $alignTop }) =>
    $alignTop &&
    css`
      & > * {
        align-self: start;
      }
    `};

  & > :last-child {
    ${({ $autoWidthField }) =>
      $autoWidthField
        ? css`
            justify-self: start;
          `
        : css`
            width: 100%;
          `};
  }
`;

export const Label = styled.label<{ $alignTop?: boolean }>`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  min-width: 0;
`;

export const Icon = styled.div`
  padding-top: 5px;
  color: ${({ theme }) => theme.palette.grey[600]};
`;

export const LabelText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  padding: 3px 0;
`;

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  padding: 5px 0;
`;

export const DateTimeFields = styled.div`
  flex: 1;
  min-width: 0;

  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

// the trigger's own popup anchor — the repeat builder opens off this box
export const TriggerAnchor = styled.div<{ $fullWidth?: boolean }>`
  min-width: 6.4rem;

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `};
`;

// stands in for the row icon on the calendar row, tinted with the calendar's own colour
export const CalendarDot = styled.span<{ $color: string }>`
  display: block;
  width: 1rem;
  height: 1rem;
  margin: 0 0.3rem;
  flex-shrink: 0;

  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

// calendar picker and availability share one line; the picker takes the slack
export const CalendarFields = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  min-width: 0;

  & > :first-child {
    flex: 1;
    min-width: 0;
  }
`;

export const WarningBanner = styled.div`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};

  margin: ${({ theme }) => `0 ${theme.spacing(4)} ${theme.spacing(4)}`};
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.spacing(1)};
  background-color: ${({ theme }) => theme.palette.warning.light};
  color: ${({ theme }) => theme.palette.warning.dark};
`;

// alignTop pins content to the top so the first line lands in the same place whether it wraps or not
export const Value = styled.div<{ $alignTop?: boolean }>`
  display: flex;
  align-items: flex-start;
  width: 100%;
`;

// a row whose value is still loading — sized to the control it replaces so the
// popup does not reflow when the detail fetch lands
export const FieldSkeleton = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 3rem;
`;

export const DescriptionField = styled.div`
  width: 100%;
  min-width: 0;
  padding: 3px 0;
`;

export const ReadOnlyValue = styled.div`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  min-width: 0;
  overflow-wrap: anywhere;
  padding: 3px 0;
  // provider descriptions carry their own line breaks; the default collapses them into one run
  white-space: pre-wrap;
  color: ${({ theme }) => theme.palette.text.primary};
`;

// spans the whole value column so a click beside the maps link still reopens the input
export const LocationReadView = styled.div<{ $canEdit: boolean }>`
  display: flex;
  align-items: flex-start;
  flex: 1;
  align-self: stretch;
  min-width: 0;
  padding: 3px 0;

  ${({ $canEdit }) =>
    $canEdit &&
    css`
      cursor: text;
    `};
`;

// keeps the link inline; a flex item would blockify and swallow the click beside it
export const LocationText = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  width: 100%;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.palette.text.primary};
`;

// reads as the address it is, not as a link — hovering is what reveals it opens maps
export const LocationLink = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.text.primary};

  &:hover {
    text-decoration: underline;
  }
`;

// The video call row stacks: which conferencing system minted the link, the link itself, and — only
// where a swap is possible — the action or the note saying one is armed.
export const VideoCallValue = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  min-width: 0;
`;

export const VideoCallProvider = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.body2)};
  flex: 1;
  min-width: 0;
`;

// names the conference rather than reading as a link; hovering is what reveals it opens one
export const VideoCallLink = styled.a`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  min-width: 0;
  overflow-wrap: anywhere;
  text-decoration: none;
  color: ${({ theme }) => theme.palette.text.primary};
  padding: 3px 0;

  &:hover {
    text-decoration: underline;
  }
`;

export const VideoCallCode = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  min-width: 0;
  margin-left: 6px;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

// pins the actions to the end and anchors their popup; the button inherits this colour from here
export const VideoCallCopy = styled.div`
  position: relative;
  margin-left: auto;
  flex-shrink: 0;
  color: ${({ theme }) => theme.palette.grey[600]};
`;

// stacks the name/code/copy line above the copied confirmation
export const VideoCallCompact = styled.div`
  flex: 1;
  min-width: 0;

  display: flex;
  flex-direction: column;
`;

export const VideoCallCompactLine = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

// opacity only: a transform here would be a vestibular trigger, and the fade reads the same
const fadeAway = keyframes`
  from { opacity: 0; }
  10%, 70% { opacity: 1; }
  to { opacity: 0; }
`;

// onAnimationEnd clears the message, so never disable this animation — the line would stay forever
export const VideoCallCopied = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.overline)};
  animation: ${fadeAway} 2s ease forwards;
`;

export const VideoCallNote = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.caption)};

  color: ${({ theme }) => theme.palette.text.secondary};
`;

// pulls the text button back in line with the link above it, which carries no button padding
export const VideoCallAction = styled.div`
  // margin-left: -8px;
`;
